import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import socketService from '@/services/socket';
import { favoritesEventEmitter } from '@/services/favoritesEventEmitter';
import { favoritesCache } from '@/services/favoritesCache';

export const useFavoriteChannels = () => {
  const [favoriteChannels, setFavoriteChannels] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch favorites from server
  const fetchFavoriteChannels = useCallback(async () => {
    try {
      setLoading(true);

      // Try cache first
      const cachedIds = favoritesCache.getFavoriteIds();
      if (cachedIds) {
        setFavoriteChannels(cachedIds);
        setLoading(false);
        return;
      }

      // Fetch from server
      const response = await api.get('/favorite-channels');
      const favorites = response.data.favoriteChannels || [];

      // Update cache
      favoritesCache.set(favorites);

      const favoriteIds = new Set<string>(favorites.map((fav: any) => fav.channelId));
      setFavoriteChannels(favoriteIds);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Error fetching favorite channels:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (channelId: string) => {
      try {
        const isFavorited = favoriteChannels.has(channelId);

        if (isFavorited) {
          await api.delete(`/favorite-channels/${channelId}`);
        } else {
          await api.post(`/favorite-channels/${channelId}`);
        }

        // Clear cache to force refetch
        favoritesCache.clear();

        // Refetch from server to ensure consistency
        await fetchFavoriteChannels();

        // Emit local event to sync across all connected clients
        favoritesEventEmitter.emit();
      } catch (error: any) {
        console.error('Error toggling favorite:', error);
        throw error;
      }
    },
    [favoriteChannels, fetchFavoriteChannels],
  );

  // Set up listeners for real-time sync
  useEffect(() => {
    const handleFavoriteUpdate = () => {
      // Clear cache and refresh favorites when any update happens
      favoritesCache.clear();
      fetchFavoriteChannels();
    };

    // Listen for local events (from same client, different component)
    favoritesEventEmitter.on(handleFavoriteUpdate);
    // Listen for remote events (from other clients)
    socketService.on('user:favorite-updated', handleFavoriteUpdate);

    return () => {
      favoritesEventEmitter.off(handleFavoriteUpdate);
      socketService.off('user:favorite-updated', handleFavoriteUpdate);
    };
  }, [fetchFavoriteChannels]);

  // Load favorites on mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      fetchFavoriteChannels();
    }
  }, [fetchFavoriteChannels]);

  return {
    favoriteChannels,
    toggleFavorite,
    fetchFavoriteChannels,
    loading,
  };
};
